import { useMemo } from 'react';
import { playlist } from '@/config/media';
import type { Track } from '@/config/media';
import { useRecycleStore } from '@/recycle/store';
import { useFsVersion } from './mutate';
import { list, resolve } from './resolve';
import { MY_COMPUTER_PATH } from '@/config/filesystem';
import type { FsNode } from './types';

/**
 * The music library, read off the disk.
 *
 * `config/media.ts` holds what a track *is* — its tags, its bytes, its
 * duration. This module answers the other question: which of them are still
 * there. Both players ask it rather than importing the list directly, so a
 * track deleted in Explorer is a track Winamp no longer has, one restored from
 * the Recycle Bin comes back, and emptying the bin takes it for good.
 *
 * Everything here goes through `fs.list()`, which already hides deleted and
 * hidden nodes — so a folder in the bin takes the music inside it with it,
 * without this file knowing the bin exists.
 */

/** Track id → track. The catalogue; the disk decides what is on it. */
const byId = new Map(playlist.map((track) => [track.id, track]));

/** The track a node holds, if it holds one at all. */
export function trackFor(node: FsNode): Track | undefined {
  if (node.type !== 'file' || node.data.kind !== 'audio') return undefined;
  return byId.get(node.data.trackId);
}

/**
 * The sound files in one folder, in the order the folder lists them. This is
 * what a `.m3u` sitting in that folder names, which is why it is separate from
 * the whole-disk sweep below.
 */
export function tracksIn(path: string): Track[] {
  const found: Track[] = [];
  for (const node of list(path)) {
    const track = trackFor(node);
    if (track) found.push(track);
  }
  return found;
}

/**
 * Every track on the disk, in the order a depth-first walk finds them — which
 * for a fresh install is My Music, in playlist order.
 *
 * Whole-disk rather than My-Music-only on purpose: a file dragged to the
 * desktop has not stopped existing, and a player that lost it there would be
 * modelling the folder rather than the disk. Delete it and it goes.
 */
export function library(): Track[] {
  const found: Track[] = [];
  const seen = new Set<string>();

  const walk = (node: FsNode): void => {
    if (node.type !== 'folder') return;
    for (const child of list(node.path)) {
      if (child.type === 'folder') {
        walk(child);
        continue;
      }
      const track = trackFor(child);
      // Two copies of one file are still one track: the players key by id,
      // and a duplicate would give the same track two rows and one highlight.
      if (track && !seen.has(track.id)) {
        seen.add(track.id);
        found.push(track);
      }
    }
  };

  const root = resolve(MY_COMPUTER_PATH);
  if (root) walk(root);
  return found;
}

/**
 * The library, as React state.
 *
 * `library()` is a plain function call, so nothing tells React its answer has
 * changed. These two subscriptions do: the bin's mask for deletes and
 * restores, the filesystem's version for moves — the same pair Explorer reads
 * for exactly the same reason.
 */
export function useLibrary(): Track[] {
  const deleted = useRecycleStore((s) => s.deleted);
  const version = useFsVersion();
  return useMemo(() => library(), [deleted, version]);
}
